// New-posting alerts tile — surfaces unseen posting_alerts rows (new
// target-role postings at priority companies, and Clef high-fit passes at any
// catalog company) at the top of Home. Hides
// itself when there's nothing unseen or the query failed: the alert is
// time-critical when present and pure noise when empty.

import { ExternalLink, X } from "lucide-react";
import { Card, CardBody, CardHeader } from "../ui/Card";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Skeleton";
import {
  useMarkAlertSeen,
  useMarkAllAlertsSeen,
  usePostingAlerts,
  type PostingAlert,
} from "../../lib/posting_alerts";

export function PostingAlertsTile() {
  const alerts = usePostingAlerts();

  if (alerts.isLoading) {
    return (
      <Card>
        <CardHeader title="New matching postings" />
        <CardBody className="space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </CardBody>
      </Card>
    );
  }

  if (alerts.error) return null;
  const rows = alerts.data?.alerts ?? [];
  if (rows.length === 0) return null;

  return <PostingAlertsCard rows={rows} />;
}

function PostingAlertsCard({ rows }: { rows: PostingAlert[] }) {
  const markAll = useMarkAllAlertsSeen();
  return (
    <Card>
      <CardHeader
        title="New matching postings"
        description="Fresh target-role postings at priority companies, plus any new posting Clef rates a strong fit. Apply within 48 hours."
        action={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => markAll.mutate()}
            loading={markAll.isPending}
          >
            Dismiss all
          </Button>
        }
      />
      <CardBody>
        <ul className="space-y-2">
          {rows.map((a) => (
            <AlertRow key={a.id} alert={a} />
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function AlertRow({ alert }: { alert: PostingAlert }) {
  const seen = useMarkAlertSeen();
  return (
    <li className="flex items-center justify-between gap-2 border-t border-neutral-800 pt-2 first:border-t-0 first:pt-0">
      <div className="min-w-0">
        <p className="truncate text-sm text-neutral-200">{alert.title}</p>
        <p className="truncate text-xs text-neutral-500">
          {alert.company_name ?? alert.company_id}
          {alert.location ? ` · ${alert.location}` : ""}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <a href={alert.url} target="_blank" rel="noreferrer">
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<ExternalLink size={12} aria-hidden />}
          >
            Open
          </Button>
        </a>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => seen.mutate(alert.id)}
          loading={seen.isPending}
          title="Dismiss"
          aria-label={`Dismiss alert for ${alert.title}`}
        >
          <X size={13} aria-hidden />
        </Button>
      </div>
    </li>
  );
}
